import { observer } from "mobx-react-lite";

import { useCustomParametersStore } from "../../../custom-parameters-store";
import { formatDropdownNames, getParameterValue } from "../../utils";
import { BuilderProps } from "../types";

import { AddNewOptionsInfoDialog } from "./AddNewOptionsInfoDialog";

import { Label } from "@/components/ui/label";
import TagInput from "@/components/ui/TagInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SchemaDefinition } from "@/types/prompt-blueprint";
import EditJsonSchemaModal from "@/components/ModelParameters/AdvancedParameters/ParameterControl/EditJsonSchemaModal";
import { isJsonSchema } from "@/components/ModelParameters/AdvancedParameters/ParameterControl/ParameterSelect/utils";
import {
  DropdownValue,
  JsonSchemaDropdownValue,
} from "@/components/ModelParameters/AdvancedParameters/ParameterControl/ParameterSelect/types";
import { ParameterValue } from "@/components/ModelParameters/utils";

const NULL_DEFAULT_SELECT_VALUE = "__promptlayer_null_default__";

const getDefaultSelectValue = (value: ParameterValue | undefined): string => {
  if (value === null || value === undefined) {
    return NULL_DEFAULT_SELECT_VALUE;
  }

  if (typeof value === "string") {
    return value;
  }

  return JSON.stringify(value);
};

export const DropdownParameterBuilder = observer(({ parameter, isViewing }: BuilderProps) => {
  const store = useCustomParametersStore();
  const { validationErrors } = store;

  const handleDefaultValueChange = (selectedValue: string) => {
    const selectedOption = parameter.options?.find(
      opt => getDefaultSelectValue(opt.value as ParameterValue) === selectedValue,
    );

    if (!selectedOption) {
      return;
    }

    parameter.updateDefault(selectedOption.value as ParameterValue);
  };

  const handleAddOption = (value: string[]) => {
    if (value.length > 0 && value.length < (parameter.options?.length || 0)) {
      const deletedOption = parameter.options?.filter(
        opt => !value.includes(opt.display_name.toLowerCase()),
      )[0];

      if (
        parameter.default &&
        typeof parameter.default === "object" &&
        "type" in parameter.default &&
        parameter.default.type === deletedOption?.value?.type
      ) {
        parameter.updateDefault(getParameterValue(value[0]));
      }
    } else {
      parameter.updateDefault(null);
    }

    parameter.updateOptions(
      value.map(opt => ({
        display_name: formatDropdownNames(opt),
        value: getParameterValue(opt),
      })),
    );
    store.clearValidationError("options");
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor="options" className="text-sm font-medium">
          Options
        </Label>
        <AddNewOptionsInfoDialog />
      </div>
      <TagInput
        initialTags={parameter.options?.map(opt => opt.display_name) || []}
        disabled={isViewing}
        shouldNormalizeTags
        onTagsChange={handleAddOption}
      />
      {validationErrors.options && (
        <span className="text-xs text-red-500">{validationErrors.options}</span>
      )}
      <p className="text-xs text-gray-500">
        Press Enter, Tab or comma to add an option. Click on a tag to remove it.
      </p>
      <div className="flex items-end gap-2">
        <div className="flex flex-col gap-2">
          <Label htmlFor="default" className="text-sm font-medium">
            Default Value
          </Label>
          <Select
            disabled={isViewing || !parameter.options?.length}
            value={getDefaultSelectValue(parameter.default)}
            onValueChange={handleDefaultValueChange}
          >
            <SelectTrigger className="w-full max-w-[200px]">
              <SelectValue placeholder="Select a value" />
            </SelectTrigger>
            <SelectContent>
              {parameter.options?.map((opt, index) => (
                <SelectItem
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${getDefaultSelectValue(opt.value as ParameterValue)}-${opt.display_name}-${index}`}
                  value={getDefaultSelectValue(opt.value as ParameterValue)}
                >
                  {formatDropdownNames(opt.display_name)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isJsonSchema(parameter.default as DropdownValue) && (
          <div className="flex flex-col gap-2">
            <EditJsonSchemaModal
              initialValues={{
                json: (parameter.default as JsonSchemaDropdownValue).json_schema,
              }}
              readonly={isViewing}
              handleSubmit={(schema: SchemaDefinition) => {
                parameter.updateDefault({
                  type: "json_schema",
                  json_schema: schema,
                });
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
});
